import { useEffect } from "react";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

import { useDraftsStore } from "../../../stores/drafts-store.js";
import { useMediaStore } from "../../../stores/media-store.js";

/**
 * Loads the queued `initialState` from the drafts store into the editor
 * exactly once, then clears the queue. Used both on first mount (recovery
 * from localStorage backup) and when the user opens an existing draft
 * from the list.
 *
 * Also hydrates the media slot from `initialMedia` so each draft owns
 * its own attachment — switching drafts replaces / clears the slot
 * rather than carrying the previous draft's media forward.
 */
export function useInitialState(): void {
  const [editor] = useLexicalComposerContext();
  const initial = useDraftsStore((s) => s.initialState);
  const initialMedia = useDraftsStore((s) => s.initialMedia);
  const consume = useDraftsStore((s) => s.consumeInitialState);
  const revision = useDraftsStore((s) => s.loadedRevision);

  useEffect(() => {
    if (!initial) return;
    try {
      const parsed = editor.parseEditorState(JSON.stringify(initial));
      editor.setEditorState(parsed);
    } catch {
      // Bad state — discard and start fresh.
    }
    useMediaStore.getState().restoreFromDraft(initialMedia);
    consume();
    // We deliberately depend on `revision` so re-loading the same draft
    // (open → switch → reopen) re-triggers.
  }, [editor, initial, initialMedia, revision, consume]);
}
